import { useEffect, useState, useCallback, useRef } from "react";

/**
 * useDraft — persists modal form state to localStorage so accidental dismissals
 * (backdrop click, tab close, refresh) don't wipe what the user typed.
 *
 * Usage:
 *   const [form, setForm, clearDraft] = useDraft("client", client?.id || "new", initialForm);
 *   <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
 *   // On successful save:  await api.post(...); clearDraft();
 *
 * @param {string} bucket  Logical group, e.g. "client", "job-new", "daily-entry".
 * @param {string} key     Sub-key (entity id, or "new" for create flows).
 * @param {object} initial Initial form value when no draft is in localStorage.
 */
export default function useDraft(bucket, key, initial) {
  const storageKey = `draft:${bucket}:${key}`;

  // initialise from localStorage if present, else from `initial`
  const [state, setStateRaw] = useState(() => {
    if (typeof window === "undefined") return initial;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return initial;
      const parsed = JSON.parse(raw);
      // Merge with the latest `initial` so newly-added fields default correctly.
      return { ...initial, ...parsed };
    } catch {
      return initial;
    }
  });

  // wrap setter so every write also persists to localStorage
  const setState = useCallback((updater) => {
    setStateRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {/* quota exceeded / private mode — ignore */}
      return next;
    });
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    try { window.localStorage.removeItem(storageKey); } catch {/* ignore */}
  }, [storageKey]);

  // expose a ref for callers that want to read latest state in effects without re-renders
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  return [state, setState, clearDraft, stateRef];
}
